import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button/Index';
import LanguageToggle from 'components/LanguageToggle';
import { TEST_ACTIVATION_ROUTE } from 'core/constants/activationTest';
import { useActivationTestMode } from 'core/context/ActivationTestMode';
import { useIsActivationFlow } from 'core/context/ActivationFlow';
import { IUser } from 'core/interface/user';
import { ActivationTestDocument } from 'core/interface/activationTestPayload';
import useLocales from 'hooks/useLocales';
import {
  buildCompanyActivationRequests,
  buildRecruiterActivationRequests
} from 'utils/activationTestPayload';

import './Index.scss';

interface TestPayloadPreviewProps {
  me: IUser;
  isCompany: boolean;
  isRecruiter: boolean;
  companyData?: Record<string, any>;
}

const TestPayloadPreview = ({
  me,
  isCompany,
  isRecruiter,
  companyData
}: TestPayloadPreviewProps): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { flow, requests } = useActivationTestMode();
  const showLanguageToggle = useIsActivationFlow();
  const [copied, setCopied] = useState(false);

  const documentPayload = useMemo<ActivationTestDocument>(() => {
    const assembledRequests = isCompany
      ? buildCompanyActivationRequests(companyData, me)
      : isRecruiter
        ? buildRecruiterActivationRequests(companyData, me)
        : requests;

    return {
      flow,
      submitted: false,
      generatedAt: new Date().toISOString(),
      account: {
        id: me?.id,
        email: me?.email,
        role: me?.role,
        candidate_id: me?.candidate_id,
        company: me?.company
      },
      requests: assembledRequests
    };
  }, [companyData, flow, isCompany, isRecruiter, me, requests]);

  const json = JSON.stringify(documentPayload, null, 2);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `activation-${flow}-payloads.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="test-payload">
      <h1 className="test-payload__title">{t('Activation payloads')}</h1>
      <p className="test-payload__desc">
        {t(
          'Nothing was submitted. This is the JSON this flow would send to the API.'
        )}
      </p>
      <pre className="test-payload__box">{json}</pre>
      <div className="test-payload__actions">
        <Button
          className="test-payload__btn"
          type="button"
          t={t}
          title={copied ? 'Copied' : 'Copy JSON'}
          onClick={handleCopy}
        />
        <Button
          className="test-payload__btn"
          type="button"
          t={t}
          title="Download JSON"
          onClick={handleDownload}
        />
        <Button
          className="test-payload__btn test-payload__btn--ghost"
          type="button"
          t={t}
          title="Back to test hub"
          onClick={() => navigate(TEST_ACTIVATION_ROUTE)}
        />
      </div>
      {showLanguageToggle && <LanguageToggle />}
    </div>
  );
};

export default TestPayloadPreview;
